import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import 'react-pivottable/pivottable.css';
import TableRenderers from 'react-pivottable/TableRenderers';
import Plot from 'react-plotly.js';
import createPlotlyRenderers from 'react-pivottable/PlotlyRenderers';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';


const PlotlyRenderers = createPlotlyRenderers(Plot);

const BillingPivotTable = ({ 
  open, 
  onClose, 
  activities = [], 
  selectedActivities = [] 
}) => {
  const [pivotState, setPivotState] = useState({
    rows: ['Paciente'],
    cols: ['Estado'],
    aggregatorName: 'Sum',
    vals: ['Monto Neto (€)'],
    rendererName: 'Table'
  });

  const [isFullscreen, setIsFullscreen] = useState(false);

  // 🔥 AQUÍ PERSONALIZAS QUÉ CAMPOS QUIERES MOSTRAR
  const visibleFields = [
    'Paciente',
    'Médico',
    //'Centro',
    'Pagador Principal',
    'Despacho Médico',
    'Producto/Servicio',
    'Fecha',
    'Mes',
    'Año',
    'Trimestre',
    'Tipo de Actividad',
    'Estado',
    'Tipo de Pagador',
    'Monto Neto (€)',
    //'Facturado (€)',
    //'Pendiente (€)',
    //'Cantidad',
    //'Número de Actividades',
    //'% Facturado',
    //'% Pendiente',
    //'Completamente Facturado',
    //'Tiene Pendientes',
  ];

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getActivityTypeLabel = (type) => {
    const types = {
      'consultation': 'Consulta',
      'procedure': 'Procedimiento',
      'surgery': 'Cirugía',
      'diagnostic': 'Diagnóstico',
      'therapy': 'Terapia',
      'emergency': 'Emergencia',
      'other': 'Otro'
    };
    return types[type] || 'Otro';
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'pending': 'Pendiente',
      'draft': 'Borrador',
      'billed': 'Facturado',
      'error': 'Error',
      'non_billable': 'No Facturable'
    };
    return statuses[status] || 'Desconocido';
  };

  const pivotData = useMemo(() => {
    if (!Array.isArray(activities) || activities.length === 0) {
      return [];
    }

    const dataToUse = selectedActivities?.length > 0 
      ? activities.filter(a => a?.id && selectedActivities.includes(a.id))
      : activities;

    return dataToUse.map(activity => {
      if (!activity) return null;

      let mes = 'Sin Fecha';
      let año = 'Sin Año';
      let trimestre = 'Sin Trimestre';
      
      if (activity.performed_at_raw) {
        try {
          const date = new Date(activity.performed_at_raw);
          const month = date.getMonth();
          mes = date.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
          año = date.getFullYear().toString();
          trimestre = `Q${Math.floor(month / 3) + 1} ${año}`;
        } catch (e) {
          console.warn('Error parseando fecha:', e);
        }
      }

      const netAmount = parseFloat(activity.net_amount) || 0;
      const billedNet = parseFloat(activity.billed_net) || 0;
      const remainingNet = parseFloat(activity.remaining_net) || 0;

      const allFields = {
        'Paciente': activity.patient_name || 'Sin Paciente',
        'Médico': activity.doctor_name || 'Sin Médico',
        'Centro': activity.center_name || 'Sin Centro',
        'Pagador Principal': activity.primary_payer_name || 'Sin Pagador',
        'Despacho Médico': activity.despacho_medico_name || 'Sin Despacho',
        'Código Despacho': activity.despacho_medico_code || 'N/A',
        'Producto/Servicio': activity.producto_cita_name || getActivityTypeLabel(activity.activity_type) || 'Sin Producto',
        'Código Producto': activity.producto_cita_code || 'N/A',
        'Fecha': activity.performed_at || 'Sin Fecha',
        'Mes': mes,
        'Año': año,
        'Trimestre': trimestre,
        'Tipo de Actividad': getActivityTypeLabel(activity.activity_type),
        'Estado': getStatusLabel(activity.status),
        'Tipo de Pagador': activity.primary_payer_type === 'User' 
          ? 'Paciente' 
          : activity.primary_payer_type === 'Company' 
            ? 'Empresa' 
            : 'Otro',
        'Monto Neto (€)': netAmount,
        'Monto Bruto (€)': parseFloat(activity.gross_amount) || 0,
        'Impuestos (€)': parseFloat(activity.tax_amount) || 0,
        'Facturado (€)': billedNet,
        'Pendiente (€)': remainingNet,
        'Precio Unitario (€)': parseFloat(activity.unit_price) || 0,
        'Precio Base Producto (€)': parseFloat(activity.producto_cita_price) || 0,
        'Cantidad': parseInt(activity.quantity) || 1,
        'Divisiones de Pago': parseInt(activity.split_count) || 0,
        'Facturas Asociadas': parseInt(activity.so_links_count) || 0,
        '% Pagador': parseFloat(activity.primary_payer_percentage) || 0,
        '% Facturado': netAmount > 0 ? (billedNet / netAmount * 100) : 0,
        '% Pendiente': netAmount > 0 ? (remainingNet / netAmount * 100) : 0,
        'Con Autorización': activity.has_authorization ? 'Sí' : 'No',
        'Completamente Facturado': activity.status === 'billed' ? 'Sí' : 'No',
        'Tiene Pendientes': remainingNet > 0 ? 'Sí' : 'No',
        'Tiene Producto Asignado': activity.has_product_info ? 'Sí' : 'No',
        'Tiene Despacho Asignado': activity.despacho_medico_name ? 'Sí' : 'No',
        'Número de Actividades': 1
      };

      const filteredFields = {};
      visibleFields.forEach(field => {
        if (field in allFields) {
          filteredFields[field] = allFields[field];
        }
      });

      return filteredFields;
    }).filter(item => item !== null);
  }, [activities, selectedActivities]);

  const presetConfigs = [
    {
      name: 'Por Paciente y Estado',
      icon: '👤',
      config: {
        rows: ['Paciente'],
        cols: ['Estado'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table'
      }
    },
    {
      name: 'Por Médico y Tipo',
      icon: '⚕️',
      config: {
        rows: ['Médico'],
        cols: ['Tipo de Actividad'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table Heatmap'
      }
    },
    {
      name: 'Evolución Mensual',
      icon: '📅',
      config: {
        rows: ['Mes'],
        cols: ['Estado'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Stacked Bar Chart'
      }
    },
    {
      name: 'Por Centro y Mes',
      icon: '🏥',
      config: {
        rows: ['Centro'],
        cols: ['Mes'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table Heatmap'
      }
    },
    {
      name: 'Top Pagadores',
      icon: '💰',
      config: {
        rows: ['Pagador Principal'],
        cols: [],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Horizontal Bar Chart'
      }
    },
    {
      name: 'Pendientes por Paciente',
      icon: '⏳',
      config: {
        rows: ['Paciente'],
        cols: ['Tiene Pendientes'],
        vals: ['Pendiente (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table'
      }
    },
    {
      name: 'Análisis por Trimestre',
      icon: '📊',
      config: {
        rows: ['Trimestre'],
        cols: ['Tipo de Actividad'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Grouped Bar Chart'
      }
    },
    {
      name: 'Distribución por Tipo',
      icon: '🥧',
      config: {
        rows: [],
        cols: ['Tipo de Actividad'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Pie Chart'
      }
    },
    {
      name: 'Por Despacho Médico',
      icon: '🏥',
      config: {
        rows: ['Despacho Médico'],
        cols: ['Estado'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table Heatmap'
      }
    },
    {
      name: 'Despacho por Mes',
      icon: '📅',
      config: {
        rows: ['Despacho Médico'],
        cols: ['Mes'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table Heatmap'
      }
    },
    {
      name: 'Productos/Servicios',
      icon: '📦',
      config: {
        rows: ['Producto/Servicio'],
        cols: ['Estado'],
        vals: ['Monto Neto (€)'],
        aggregatorName: 'Sum',
        rendererName: 'Table'
      }
    },
    {
      name: 'Médico por Despacho',
      icon: '⚕️',
      config: {
        rows: ['Médico'],
        cols: ['Despacho Médico'],
        vals: ['Número de Actividades'],
        aggregatorName: 'Sum',
        rendererName: 'Table Heatmap'
      }
    },
    {
      name: 'Cobertura de Productos',
      icon: '📊',
      config: {
        rows: ['Tiene Producto Asignado'],
        cols: ['Tiene Despacho Asignado'],
        vals: ['Número de Actividades'],
        aggregatorName: 'Sum',
        rendererName: 'Table'
      }
    }
  ];

  const applyPreset = (preset) => {
    setPivotState(preset.config);
  };

  const resetPivot = () => {
    setPivotState({
      rows: ['Paciente'],
      cols: ['Estado'],
      aggregatorName: 'Sum',
      vals: ['Monto Neto (€)'],
      rendererName: 'Table'
    });
  };

  const handleExport = (format = 'csv') => {
    try {
      if (pivotData.length === 0) {
        alert('No hay datos para exportar');
        return;
      }

      const pivotTable = document.querySelector('.pvtTable');
      if (!pivotTable) {
        alert('No se puede exportar. Asegúrate de que la tabla esté visible.');
        return;
      }

      if (format === 'csv') {
        exportToCSV(pivotTable);
      } else if (format === 'pdf') {
        exportToPDF(pivotTable);
      }
    } catch (error) {
      console.error('Error exporting:', error);
      alert(`Error al exportar a ${format.toUpperCase()}: ${error.message}`);
    }
  };

  const cleanCellValue = (text) => {
    if (!text) return '';
    
    return text
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/^\s*€\s*/, '')
      .replace(/\s*€\s*$/, '');
  };

  const exportToCSV = (table) => {
    const rows = Array.from(table.querySelectorAll('tr'));
    const csvData = [];
    
    rows.forEach(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const rowData = [];
      
      cells.forEach(cell => {
        const colspan = parseInt(cell.getAttribute('colspan')) || 1;
        let value = cleanCellValue(cell.textContent);
        
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        
        rowData.push(value);
        
        for (let i = 1; i < colspan; i++) {
          rowData.push('');
        }
      });
      
      if (rowData.some(v => v !== '')) {
        csvData.push(rowData);
      }
    });
    
    const csv = csvData.map(row => row.join(',')).join('\n');
    
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-');
    downloadFile(csv, `tabla_pivote_${timestamp}.csv`, 'text/csv');
  };

  const exportToPDF = (table) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Tabla Dinámica de Facturación', 14, 15);
    
    if (stats) {
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      doc.text(
        `${stats.count} actividades | Total: ${stats.totalNet.toFixed(2)}€ | ` +
        `Facturado: ${stats.totalBilled.toFixed(2)}€ | Pendiente: ${stats.totalPending.toFixed(2)}€`,
        14, 22
      );
    }
    
    const rows = Array.from(table.querySelectorAll('tr'));
    const tableData = [];
    
    rows.forEach(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const rowData = [];
      
      cells.forEach(cell => {
        const colspan = parseInt(cell.getAttribute('colspan')) || 1;
        let value = cleanCellValue(cell.textContent);
        
        rowData.push(value);
        
        for (let i = 1; i < colspan; i++) {
          rowData.push('');
        }
      });
      
      if (rowData.some(v => v !== '')) {
        tableData.push(rowData);
      }
    });
    
    if (tableData.length === 0) {
      alert('No hay datos para exportar');
      return;
    }
    
    autoTable(doc, {
      head: [tableData[0]],
      body: tableData.slice(1),
      startY: 28,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 8
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 28, left: 10, right: 10, bottom: 15 },
      theme: 'grid',
      didDrawPage: function(data) {
        doc.setFontSize(8);
        doc.setTextColor(150);
        const pageCount = doc.internal.pages.length - 1;
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
        
        doc.text(
          new Date().toLocaleString('es-ES'),
          doc.internal.pageSize.width - 10,
          doc.internal.pageSize.height - 10,
          { align: 'right' }
        );
      }
    });
    
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(/:/g, '-');
    doc.save(`tabla_pivote_${timestamp}.pdf`);
  };

  const downloadFile = (content, filename, mimeType) => {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + content], { type: `${mimeType};charset=utf-8;` });
    const link = document.createElement('a');
    
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const stats = useMemo(() => {
    if (pivotData.length === 0) return null;
    
    const totalNet = pivotData.reduce((sum, row) => sum + (row['Monto Neto (€)'] || 0), 0);
    const totalBilled = pivotData.reduce((sum, row) => sum + (row['Facturado (€)'] || 0), 0);
    const totalPending = pivotData.reduce((sum, row) => sum + (row['Pendiente (€)'] || 0), 0);
    
    return {
      totalNet,
      totalBilled,
      totalPending,
      count: pivotData.length
    };
  }, [pivotData]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { 
          height: isFullscreen ? '100vh' : '95vh',
          maxWidth: isFullscreen ? '100vw' : '95vw'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BarChartIcon color="primary" />
          <Box>
            <Typography variant="h6">
              Tabla Dinámica de Facturación
            </Typography>
            {stats && (
              <Typography variant="caption" color="textSecondary">
                {stats.count} actividades • Total: {stats.totalNet.toFixed(2)}€ • 
                Facturado: {stats.totalBilled.toFixed(2)}€ • 
                Pendiente: {stats.totalPending.toFixed(2)}€
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={toggleFullscreen} size="small" title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton onClick={resetPivot} size="small" title="Resetear vista">
            <RefreshIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleExport('csv')} 
            size="small" 
            title="Exportar a CSV" 
            disabled={pivotData.length === 0}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              ⚡ Configuraciones Rápidas
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexWrap: 'wrap',
              maxHeight: '120px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pb: 1
            }}>
              {presetConfigs.map((preset, index) => (
                <Chip
                  key={index}
                  label={`${preset.icon} ${preset.name}`}
                  onClick={() => applyPreset(preset)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  clickable
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {pivotData.length === 0 ? (
  <Alert severity="warning" sx={{ mt: 2 }}>
    <Typography variant="body2" gutterBottom>
      <strong>No hay datos disponibles</strong>
    </Typography>
    <Typography variant="caption">
      Verifica que haya actividades cargadas en la tabla principal o que hayas seleccionado alguna actividad.
    </Typography>
  </Alert>
) : (
  <Box sx={{ 
  border: '1px solid #e0e0e0', 
  borderRadius: 1,
  overflow: 'auto',
  height: isFullscreen ? '100vh' : 'calc(95vh - 380px)',
  backgroundColor: '#fafafa',
  // 🔥 Modo fullscreen solo para la pivot table
  ...(isFullscreen && {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 9999,
    borderRadius: 0,
    margin: 0,
    padding: '60px 20px 20px 20px',
    border: 'none',
    overflowX: 'scroll',
    overflowY: 'auto'
  })
}}>
    {/* 🔥 Header fijo cuando está en fullscreen */}
    {isFullscreen && (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '50px',
        backgroundColor: 'white',
        borderBottom: '2px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        zIndex: 10000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BarChartIcon color="primary" />
          Tabla Dinámica - Pantalla Completa
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {stats && (
            <Typography variant="caption" color="textSecondary" sx={{ mr: 2 }}>
              {stats.count} actividades • Total: {stats.totalNet.toFixed(2)}€
            </Typography>
          )}
          <IconButton 
            onClick={() => handleExport('csv')} 
            size="small"
            title="Exportar CSV"
            sx={{ backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' } }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen} 
            size="small"
            title="Salir de pantalla completa"
            sx={{ 
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' }
            }}
          >
            <FullscreenExitIcon />
          </IconButton>
        </Box>
      </Box>
    )}
              <style>
  {`
    /* CONFIGURACIÓN GENERAL DEL LAYOUT */
    .pvtUi {
      display: grid !important;
      grid-template-columns: 280px 1fr !important;
      grid-template-rows: auto auto 1fr !important;
      gap: 12px !important;
      height: 100% !important;
      padding: 12px !important;
      background-color: #ffffff !important;
    }

    /* 🔥 NUEVO: Comportamiento especial en fullscreen */
    .pvtUi.fullscreen-mode {
      width: 100vw !important;
      height: calc(100vh - 60px) !important;
      margin-top: 60px !important;
      grid-template-columns: 280px 1fr !important;
    }
    
    .pvtUi.fullscreen-mode .pvtUiCell {
      width: 100% !important;
      max-width: 100% !important;
      overflow: visible !important;
    }
    
    .pvtUi.fullscreen-mode .pvtOutput {
      overflow-x: auto !important;
      overflow-y: auto !important;
      max-width: 100% !important;
      width: 100% !important;
      height: calc(100vh - 200px) !important;
    }
    
    .pvtUi.fullscreen-mode .pvtTable {
      min-width: max-content !important;
      width: auto !important;
    }
    
    /* Selector de tabla */
    .pvtRenderers {
      grid-column: 1 !important;
      grid-row: 1 !important;
      padding: 10px !important;
      background: #f8f8f8 !important;
      border: 1px solid #ddd !important;
      border-radius: 4px !important;
    }
    
    .pvtRenderers select {
      width: 100% !important;
      padding: 8px !important;
      border: 1px solid #ccc !important;
      border-radius: 4px !important;
      font-size: 13px !important;
      background-color: white !important;
      color: #333 !important;
      cursor: pointer !important;
    }
    
    .pvtRenderers select:hover {
      border-color: #999 !important;
    }
    
    .pvtRenderers select:focus {
      outline: none !important;
      border-color: #666 !important;
    }
    
    /* Agregador y valores */
    .pvtAxisContainer, .pvtVals {
      grid-column: 1 !important;
      grid-row: 2 !important;
      padding: 10px !important;
      background-color: #f8f8f8 !important;
      border: 1px solid #ddd !important;
      border-radius: 4px !important;
    }
    
    .pvtAxisContainer select, .pvtVals select {
      width: 100% !important;
      padding: 6px !important;
      border: 1px solid #ccc !important;
      border-radius: 4px !important;
      font-size: 12px !important;
      margin-top: 4px !important;
    }
    
    /* CAMPOS DISPONIBLES - CON SCROLL */
    .pvtUnused {
      grid-column: 1 !important;
      grid-row: 3 !important;
      height: 350px !important;
      max-height: 350px !important;
      min-height: 350px !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      padding: 12px !important;
      background-color: #ffffff !important;
      border: 1px solid #ddd !important;
      border-radius: 4px !important;
    }
    
    .pvtUnused::before {
      content: "Campos Disponibles" !important;
      display: block !important;
      font-size: 12px !important;
      font-weight: 600 !important;
      color: #666 !important;
      margin-bottom: 10px !important;
      padding-bottom: 8px !important;
      border-bottom: 1px solid #eee !important;
    }
    
    /* Scroll vertical campos */
    .pvtUnused::-webkit-scrollbar {
      width: 8px !important;
    }
    
    .pvtUnused::-webkit-scrollbar-track {
      background: #f1f1f1 !important;
      border-radius: 4px !important;
    }
    
    .pvtUnused::-webkit-scrollbar-thumb {
      background: #888 !important;
      border-radius: 4px !important;
    }
    
    .pvtUnused::-webkit-scrollbar-thumb:hover {
      background: #555 !important;
    }
    
    /* ESTILOS PARA CAMPOS */
    .pvtAttr {
      margin: 4px 3px !important;
      padding: 6px 12px !important;
      display: inline-block !important;
      font-size: 11px !important;
      font-weight: 500 !important;
      background: #f5f5f5 !important;
      border: 1px solid #ccc !important;
      border-radius: 4px !important;
      cursor: grab !important;
      line-height: 1.3 !important;
      transition: all 0.2s ease !important;
      color: #333 !important;
    }
    
    .pvtAttr:hover {
      background: #e8e8e8 !important;
      border-color: #999 !important;
    }
    
    .pvtAttr:active {
      cursor: grabbing !important;
    }
    
    /* Campos en FILAS */
    .pvtRows .pvtAttr {
      background: #f0f0f0 !important;
      border-color: #aaa !important;
      font-weight: 600 !important;
    }
    
    /* Campos en COLUMNAS */
    .pvtCols .pvtAttr {
      background: #f0f0f0 !important;
      border-color: #aaa !important;
      font-weight: 600 !important;
    }
    
    /* Campos en VALORES */
    .pvtVals .pvtAttr {
      background: #f0f0f0 !important;
      border-color: #aaa !important;
      font-weight: 600 !important;
    }
    
    /* ZONAS DE DROP - Filas y Columnas */
    .pvtRows, .pvtCols {
      min-height: 50px !important;
      padding: 12px !important;
      background-color: #fafafa !important;
      border: 1px dashed #ccc !important;
      border-radius: 4px !important;
      margin-bottom: 10px !important;
    }
    
    .pvtRows:empty::before {
      content: "Arrastra campos aquí (FILAS)" !important;
      color: #999 !important;
      font-size: 11px !important;
    }
    
    .pvtCols:empty::before {
      content: "Arrastra campos aquí (COLUMNAS)" !important;
      color: #999 !important;
      font-size: 11px !important;
    }
    
    /* ÁREA DE LA TABLA - CON SCROLL HORIZONTAL Y VERTICAL */
    .pvtUiCell {
      grid-column: 2 !important;
      grid-row: 1 / 4 !important;
      display: flex !important;
      flex-direction: column !important;
      background-color: white !important;
      border-radius: 4px !important;
      padding: 12px !important;
      border: 1px solid #ddd !important;
      overflow: hidden !important;
    }
    
    /* SCROLL HORIZONTAL Y VERTICAL VISIBLE */
    .pvtOutput {
      flex: 1 !important;
      overflow-x: scroll !important;
      overflow-y: scroll !important;
      border: 1px solid #ddd !important;
      border-radius: 4px !important;
      padding: 8px !important;
      width: 100% !important;
      height: 100% !important;
      min-width: 0 !important;
    }
    
    /* SCROLLBAR HORIZONTAL VISIBLE Y GRANDE */
    .pvtOutput::-webkit-scrollbar {
      height: 16px !important;
      width: 16px !important;
    }
    
    .pvtOutput::-webkit-scrollbar-track {
      background: #f1f1f1 !important;
      border-radius: 4px !important;
    }
    
    .pvtOutput::-webkit-scrollbar-thumb {
      background: #888 !important;
      border-radius: 4px !important;
      border: 2px solid #f1f1f1 !important;
    }
    
    .pvtOutput::-webkit-scrollbar-thumb:hover {
      background: #555 !important;
    }
    
    .pvtOutput::-webkit-scrollbar-corner {
      background: #f1f1f1 !important;
    }
    
    /* TABLA CON ANCHO AUTOMÁTICO PARA SCROLL HORIZONTAL */
    .pvtTable {
      font-size: 12px !important;
      min-width: max-content !important;
      width: auto !important;
      display: table !important;
      table-layout: auto !important;
      border-collapse: collapse !important;
    }
    
    .pvtTable tbody, .pvtTable thead {
      display: table-row-group !important;
    }
    
    .pvtTable tr {
      display: table-row !important;
    }
    
    .pvtTable th, .pvtTable td {
      display: table-cell !important;
      white-space: nowrap !important;
      padding: 8px 12px !important;
      border: 1px solid #ddd !important;
      min-width: 80px !important;
    }
    
    /* Encabezados */
    .pvtTable thead tr th {
      background-color: #f5f5f5 !important;
      color: #333 !important;
      font-weight: 600 !important;
      padding: 10px 12px !important;
      border: 1px solid #ddd !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 10 !important;
    }
    
    /* Filas alternadas */
    .pvtTable tbody tr:nth-child(even) {
      background-color: #fafafa !important;
    }
    
    .pvtTable tbody tr:hover {
      background-color: #f0f0f0 !important;
    }
    
    .pvtTable tbody tr td {
      padding: 8px 12px !important;
      color: #333 !important;
    }
    
    /* Labels */
    .pvtAxisContainer label, .pvtVals label {
      font-size: 11px !important;
      font-weight: 600 !important;
      color: #666 !important;
      display: block !important;
      margin-bottom: 6px !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
    }
    
    /* Responsive */
    @media (max-width: 1200px) {
      .pvtUi {
        grid-template-columns: 240px 1fr !important;
      }
      
      .pvtUnused {
        height: 280px !important;
        max-height: 280px !important;
      }
    }
  `}
</style>
              <PivotTableUI
      data={pivotData}
      onChange={s => setPivotState(s)}
      renderers={Object.assign({}, TableRenderers, PlotlyRenderers)}
      {...pivotState}
      locale="es"
      plotlyOptions={{
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d']
      }}
    />
  </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', gap: 1 }}>
        <Typography variant="caption" color="textSecondary" sx={{ flex: 1 }}>
          💡 Tip: Los campos en <strong style={{color: '#66bb6a'}}>verde</strong> son filas, 
          en <strong style={{color: '#ffa726'}}>naranja</strong> columnas, 
          en <strong style={{color: '#ab47bc'}}>morado</strong> valores
        </Typography>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
        <Button 
          onClick={() => handleExport('csv')} 
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          disabled={pivotData.length === 0}
        >
          Exportar CSV
        </Button>
        <Button 
          onClick={() => handleExport('pdf')} 
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          disabled={pivotData.length === 0}
        >
          Exportar PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingPivotTable;